import React, { useEffect, useState } from 'react';
import { Mail, Phone, MapPin, ArrowUpRight, CheckCircle2 } from 'lucide-react';
import { Container, EyebrowLabel, SectionTitle } from '../components/Section';
import HeroBanner from '../components/HeroBanner';
import { contactInfo } from '../mock';
import { useToast } from '../hooks/use-toast';
import { createEnquiry } from '../lib/api';

const services = ['Permanent Placement', 'Project Staffing', 'RPO Solution', 'Not sure yet'];
const industriesList = ['Semiconductor', 'Automotive', 'Aerospace', 'Industrial Engineering', 'Consumer Electronics', 'Telecom', 'Other'];

export default function Contact() {
  const { toast } = useToast();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: '', company: '', email: '', phone: '', service: 'Permanent Placement', industry: 'Semiconductor', message: '',
  });

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await createEnquiry(form);
      setSubmitted(true);
      toast({ title: 'Enquiry received', description: 'A specialist will get back to you within one business day.' });
    } catch (err) {
      toast({ title: 'Could not send enquiry', description: err?.response?.data?.detail || 'Please try again shortly.', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#FAF7F5]">
      <HeroBanner
        image="/hero/contact.jpg"
        overlay="default"
        height="min-h-[440px] lg:min-h-[500px]"
      >
        <div className="pt-[120px] lg:pt-[140px] pb-12">
          <Container>
            <div className="grid lg:grid-cols-12 gap-10 items-end erbity-fade-up">
              <div className="lg:col-span-8">
                <EyebrowLabel>Contact</EyebrowLabel>
                <SectionTitle as="h1" className="mt-6 text-[44px] lg:text-[68px] leading-[1.04]">
                  Let&rsquo;s talk about your next <span className="italic text-[#7E2E4A]">hire</span>.
                </SectionTitle>
              </div>
              <div className="lg:col-span-4 erbity-fade-up-1">
                <p className="text-[16px] text-[#3B333A] leading-relaxed">
                  Share your role and requirements &mdash; we&rsquo;ll respond within one
                  business day, and deliver a domain-vetted shortlist within 72 hours.
                </p>
              </div>
            </div>
          </Container>
        </div>
      </HeroBanner>

      <section className="pb-16 lg:pb-20">
        <Container>
          <div className="grid lg:grid-cols-12 gap-6">
            <div className="lg:col-span-8 bg-white rounded-3xl border border-[#EADDE2] p-8 lg:p-12">
              {submitted ? (
                <div className="flex flex-col items-start gap-4 py-8">
                  <div className="w-14 h-14 rounded-full bg-[#F2E3E8] flex items-center justify-center text-[#7E2E4A]">
                    <CheckCircle2 size={26} />
                  </div>
                  <h3 className="font-serif text-[28px] text-[#1A1420]" style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}>Thanks, {form.name?.split(' ')[0] || 'there'}.</h3>
                  <p className="text-[15px] text-[#4E434A] max-w-md">
                    Your enquiry is with our specialist team. Expect a response from a
                    real engineer-recruiter within one business day.
                  </p>
                  <button onClick={() => { setSubmitted(false); setForm({ name: '', company: '', email: '', phone: '', service: 'Permanent Placement', industry: 'Semiconductor', message: '' }); }} className="mt-4 text-[14px] text-[#7E2E4A] border-b border-[#7E2E4A]/40 pb-0.5">
                    Send another enquiry
                  </button>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="grid sm:grid-cols-2 gap-5">
                  <div className="sm:col-span-1">
                    <label className="text-[12px] uppercase tracking-[0.18em] text-[#7A6A72]">Your name</label>
                    <input required name="name" value={form.name} onChange={onChange} className="mt-2 w-full bg-transparent border-b border-[#EADDE2] focus:border-[#7E2E4A] outline-none py-2.5 text-[15px] text-[#1A1420] placeholder-[#B7A9AF]" placeholder="Alex Chen" />
                  </div>
                  <div className="sm:col-span-1">
                    <label className="text-[12px] uppercase tracking-[0.18em] text-[#7A6A72]">Company</label>
                    <input required name="company" value={form.company} onChange={onChange} className="mt-2 w-full bg-transparent border-b border-[#EADDE2] focus:border-[#7E2E4A] outline-none py-2.5 text-[15px] text-[#1A1420] placeholder-[#B7A9AF]" placeholder="Silicon Labs" />
                  </div>
                  <div>
                    <label className="text-[12px] uppercase tracking-[0.18em] text-[#7A6A72]">Work email</label>
                    <input required type="email" name="email" value={form.email} onChange={onChange} className="mt-2 w-full bg-transparent border-b border-[#EADDE2] focus:border-[#7E2E4A] outline-none py-2.5 text-[15px] text-[#1A1420] placeholder-[#B7A9AF]" placeholder="alex@company.com" />
                  </div>
                  <div>
                    <label className="text-[12px] uppercase tracking-[0.18em] text-[#7A6A72]">Phone (optional)</label>
                    <input name="phone" value={form.phone} onChange={onChange} className="mt-2 w-full bg-transparent border-b border-[#EADDE2] focus:border-[#7E2E4A] outline-none py-2.5 text-[15px] text-[#1A1420] placeholder-[#B7A9AF]" placeholder="+1 555 000 0000" />
                  </div>
                  <div>
                    <label className="text-[12px] uppercase tracking-[0.18em] text-[#7A6A72]">Service</label>
                    <select name="service" value={form.service} onChange={onChange} className="mt-2 w-full bg-transparent border-b border-[#EADDE2] focus:border-[#7E2E4A] outline-none py-2.5 text-[15px] text-[#1A1420]">
                      {services.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[12px] uppercase tracking-[0.18em] text-[#7A6A72]">Industry</label>
                    <select name="industry" value={form.industry} onChange={onChange} className="mt-2 w-full bg-transparent border-b border-[#EADDE2] focus:border-[#7E2E4A] outline-none py-2.5 text-[15px] text-[#1A1420]">
                      {industriesList.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-[12px] uppercase tracking-[0.18em] text-[#7A6A72]">Tell us about the role</label>
                    <textarea required rows={4} name="message" value={form.message} onChange={onChange} className="mt-2 w-full bg-transparent border-b border-[#EADDE2] focus:border-[#7E2E4A] outline-none py-2.5 text-[15px] text-[#1A1420] placeholder-[#B7A9AF] resize-none" placeholder="e.g. Looking for a Senior RTL Design Engineer with 7+ years on 7nm SoCs, based in Bengaluru or remote." />
                  </div>
                  <div className="sm:col-span-2 flex items-center justify-between pt-4">
                    <div className="text-[12px] text-[#7A6A72]">All enquiries are confidential.</div>
                    <button disabled={submitting} type="submit" className="group inline-flex items-center gap-2 bg-[#1A1420] text-white text-[15px] font-medium pl-6 pr-2 py-2 rounded-full hover:bg-[#3D1A2A] transition-colors disabled:opacity-60">
                      <span className="py-1">{submitting ? 'Sending\u2026' : 'Send enquiry'}</span>
                      <span className="w-10 h-10 rounded-full bg-[#C4506F] flex items-center justify-center group-hover:rotate-45 transition-transform duration-300">
                        <ArrowUpRight size={16} />
                      </span>
                    </button>
                  </div>
                </form>
              )}
            </div>

            <aside className="lg:col-span-4 space-y-4">
              <div className="bg-[#1A1420] text-white rounded-3xl p-8">
                <div className="text-[11px] uppercase tracking-[0.22em] text-white/50">Direct lines</div>
                <ul className="mt-6 space-y-5 text-[14.5px]">
                  <li className="flex items-start gap-3">
                    <Mail size={16} className="mt-1 text-[#E7A9B9]" />
                    <div><div className="text-white/50 text-[12px]">Email</div><div>{contactInfo.email}</div></div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Phone size={16} className="mt-1 text-[#E7A9B9]" />
                    <div><div className="text-white/50 text-[12px]">Phone</div><div>{contactInfo.phone}</div></div>
                  </li>
                  <li className="flex items-start gap-3">
                    <MapPin size={16} className="mt-1 text-[#E7A9B9]" />
                    <div><div className="text-white/50 text-[12px]">Headquarters</div><div>{contactInfo.address}</div></div>
                  </li>
                </ul>
              </div>
              <div className="bg-white rounded-3xl border border-[#EADDE2] p-8">
                <div className="text-[11px] uppercase tracking-[0.22em] text-[#7A2E48]">Delivery hubs</div>
                <p className="mt-4 text-[14.5px] text-[#1A1420]">{contactInfo.hqLine}</p>
                <p className="mt-4 text-[13px] text-[#7A6A72]">Coverage across India&rsquo;s four largest semiconductor talent clusters, with remote-first support for US, EU and APAC clients.</p>
              </div>
            </aside>
          </div>
        </Container>
      </section>
    </div>
  );
}
